/**
 * Constants and types for business groups
 * @file business-group-constants.ts
 */

import { DMMBusinessSegmentType } from 'src/models/DmmModel';

/**
 * Business group codes which requires customization.
 */
export const BUSINESS_GROUP_CODES = {
  OPD_SOFTWARE: 'SW',
  OPD_HARDWARE: 'HDW',
  OPD_OPERATIONS: 'OPS', // CapEx File Upload only for OPERATIONS Admins
  DMM: 'DMM', // Campaigns, Product Line mappings are only for DMM and some other customizations.
  RING: 'RNG', // Additional Account codes are added for RING (RNG) Business Groups
  SALES_AND_MARKETING: 'SM'
} as const;

/**
 * Type representing all possible business group code values
 */
export type BusinessGroupCode = (typeof BUSINESS_GROUP_CODES)[keyof typeof BUSINESS_GROUP_CODES];

/**
 * Interface for business group details
 */
export interface BusinessGroupDetails {
  code: BusinessGroupCode;
  name: string;
}

/**
 * Complete business group information mapping
 */
export const BUSINESS_GROUPS: Readonly<Record<BusinessGroupCode, BusinessGroupDetails>> = {
  [BUSINESS_GROUP_CODES.OPD_SOFTWARE]: {
    code: BUSINESS_GROUP_CODES.OPD_SOFTWARE,
    name: 'OPD Software'
  },
  [BUSINESS_GROUP_CODES.OPD_HARDWARE]: {
    code: BUSINESS_GROUP_CODES.OPD_HARDWARE,
    name: 'OPD Hardware'
  },
  [BUSINESS_GROUP_CODES.OPD_OPERATIONS]: {
    code: BUSINESS_GROUP_CODES.OPD_OPERATIONS,
    name: 'OPD Operations'
  },
  [BUSINESS_GROUP_CODES.DMM]: {
    code: BUSINESS_GROUP_CODES.DMM,
    name: 'DMM'
  },
  [BUSINESS_GROUP_CODES.RING]: {
    code: BUSINESS_GROUP_CODES.RING,
    name: 'RNG'
  },
  [BUSINESS_GROUP_CODES.SALES_AND_MARKETING]: {
    code: BUSINESS_GROUP_CODES.SALES_AND_MARKETING,
    name: 'SM'
  }
} as const;

// Ensure immutability
Object.freeze(BUSINESS_GROUP_CODES);
Object.freeze(BUSINESS_GROUPS);

export const DMM_BUSINESS_SEGMENT_ORDER: DMMBusinessSegmentType[] = [
  'Product Line',
  'Campaign Name',
  'Country',
  'Desired Business Impact',
  'Spend Category',
  'Efficiency Management',
  'Channel Name',
  'Revenue Estimate',
  'Business Category',
  'Campaign Owner'
];

export const DMM_FIELDS_MAPPING = {
  'Product Line': 'product_code',
  'Campaign Name': 'product_line_campaign_name',
  'Campaign Owner': 'campaign_owner',
  Country: 'country_code',
  'Revenue Estimate': 'revenue_estimate',
  'Business Category': 'business_category',
  'Spend Category': 'spendCategory',
  'Efficiency Management': 'efficiencyMeasurement',
  'Desired Business Impact': 'desiredBusinessImpact',
  'Channel Name': 'channelName'
};

export const BUSINESS_SEGMENT_OPTIONAL_FIELDS: DMMBusinessSegmentType[] = ['Revenue Estimate', 'Business Category', 'Campaign Owner'];

export const ECHO_BUSINESS_SEGMENT_OPTIONAL_FIELDS: DMMBusinessSegmentType[] = ['Campaign Owner'];

export const ECHO_PRODUCT_LINE_CODE = '3007';

/**
 * Navigation section groups for the secondary navigation bar.
 * Each section is an ordered array of data_classification_short_description values (case-insensitive).
 * Sections are rendered in the order defined here, with vertical dividers between them.
 * Any classification not in a defined section will be placed in an 'unassigned' group before DMM.
 */
export const NAV_SECTION_GROUPS: readonly string[][] = [
  ['HDW', 'SW', 'OPS', 'SUS'], // OPD group
  ['EA', 'AGI'], // Echo Alexa & AGI
  ['RNG', 'BLK', 'ER', 'ZO'], // Ring & Devices
  ['KP', 'AP', 'DFT', 'SM'], // Amazon Leo, Amazon Photos, DaS Finance, Sales & Marketing
  ['DMM'] // DMM
];

// Ensure immutability of nav sections
Object.freeze(NAV_SECTION_GROUPS);
